import { useEffect, useRef, useState } from "react";
import type { PropsWithChildren, ReactNode } from "react";

import isNil from "lodash/isNil";
import type { LayoutChangeEvent, View as ViewRN } from "react-native";
import { TouchableWithoutFeedback } from "react-native";

import SafeNativeModal from "@/ui/composed/modal/SafeNativeModal";
import { Box, VStack } from "@/ui/layout";
import { ShadowStyles } from "@/ui/theme/styles";
import { tokens } from "@/ui/theme/tamagui.config";
import { ColorToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";

const { borderColor, borderWidth } = tokens;

type Alignment = "center" | "left" | "right";

const calculateAlignment = (params: {
  alignment: Alignment;
  childrenWidth?: number;
  triggerWidth: number;
  triggerX: number;
}): number => {
  const { alignment, childrenWidth, triggerWidth, triggerX } = params;
  if (alignment === "left" || isNil(childrenWidth)) return triggerX;
  if (alignment === "right") return triggerX + triggerWidth - childrenWidth;
  return triggerX + triggerWidth / 2 - childrenWidth / 2;
};

const getDropdownOpacity = (childrenWidth?: number): number => {
  if (!isWeb()) return 1;
  return childrenWidth ? 1 : 0;
};

interface Dimensions {
  height: number;
  width: number;
  x: number;
  y: number;
}

interface DropdownMenuProps {
  alignment?: Alignment;
  children: ReactNode;
  handleClose: () => void;
  handleOpen: () => void;
  isVisible: boolean;
  paddingHorizontal?: number;
  paddingVertical?: number;
  trigger: ReactNode;
}

interface DropdownContentProps extends PropsWithChildren {
  alignment: Alignment;
  childrenWidth?: number;
  handleClose: () => void;
  onChildrenLayout: (event: LayoutChangeEvent) => void;
  paddingHorizontal?: number;
  paddingVertical: number;
  position: Dimensions;
}

const DropdownContent = ({
  alignment,
  children,
  childrenWidth,
  handleClose,
  onChildrenLayout,
  paddingHorizontal,
  paddingVertical,
  position,
}: DropdownContentProps) => {
  const { height, width, x, y } = position;

  const content = (
    <Box onLayout={onChildrenLayout}>
      <VStack
        backgroundColor={ColorToken.NeutralBackgroundPrimary}
        borderColor={borderColor.primary}
        borderRadius={8}
        borderWidth={borderWidth.primary}
        paddingHorizontal={paddingHorizontal}
        paddingVertical={paddingVertical}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...ShadowStyles}
      >
        {children}
      </VStack>
    </Box>
  );

  const leftPosition = calculateAlignment({
    alignment,
    childrenWidth,
    triggerWidth: width,
    triggerX: x,
  });

  // On web: hide until layout measured to prevent positioning shift (x=0 → x=correct)
  // animationType="none" removes modal animation, but async measurement still causes shift
  // On mobile: always visible, modal fade handles animation
  const opacity = getDropdownOpacity(childrenWidth);

  return (
    <>
      {/* Background overlay for capturing outside clicks */}
      <TouchableWithoutFeedback onPress={handleClose}>
        <Box bottom={0} left={0} position="absolute" right={0} top={0} />
      </TouchableWithoutFeedback>
      <Box
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...ShadowStyles}
        left={leftPosition}
        opacity={opacity}
        position="absolute"
        top={y + height}
      >
        {content}
      </Box>
    </>
  );
};

const DropdownMenu = ({
  alignment = "center",
  children,
  handleClose,
  handleOpen,
  isVisible,
  paddingHorizontal,
  paddingVertical = 12,
  trigger,
}: DropdownMenuProps) => {
  const [position, setPosition] = useState<Dimensions>({
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  });
  const triggerRef = useRef<ViewRN>(null);
  useEffect(() => {
    setWasJustOpened(isVisible);

    if (isNil(triggerRef.current) || !isVisible) return;

    triggerRef.current.measure(
      (
        _originX: number,
        _originY: number,
        width: number,
        height: number,
        componentX: number,
        componentY: number,
        // eslint-disable-next-line max-params
      ) => {
        setPosition({
          height,
          width,
          x: componentX,
          y: componentY,
        });
      },
    );
  }, [isVisible]);

  // only set childrenWidth when isVisible is toggled on, to prevent potential loop with left={..} changing, and childrenWidth changing
  const [wasJustOpened, setWasJustOpened] = useState<boolean>(false);
  const [childrenWidth, setChildrenWidth] = useState<number | undefined>(
    undefined,
  );
  const onChildrenLayout = (event: LayoutChangeEvent) => {
    const { layout } = event.nativeEvent;
    const { width } = layout;
    if (!wasJustOpened) return;
    setChildrenWidth(width);
    setWasJustOpened(false);
  };

  return (
    <Box>
      <Box onPress={handleOpen}>
        <Box collapsable={false} ref={triggerRef}>
          {trigger}
        </Box>
      </Box>

      {isVisible && (
        <SafeNativeModal
          animationType={isWeb() ? "none" : "fade"}
          onRequestClose={handleClose}
          transparent
          visible={isVisible}
        >
          <DropdownContent
            alignment={alignment}
            childrenWidth={childrenWidth}
            handleClose={handleClose}
            onChildrenLayout={onChildrenLayout}
            paddingHorizontal={paddingHorizontal}
            paddingVertical={paddingVertical}
            position={position}
          >
            {children}
          </DropdownContent>
        </SafeNativeModal>
      )}
    </Box>
  );
};

export default DropdownMenu;
export { calculateAlignment, getDropdownOpacity };
export type { DropdownMenuProps };
